import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProduct, useProducts } from '../hooks/useProducts';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowLeft, Check, ArrowRight, X, ChevronLeft, ChevronRight } from 'lucide-react';

const ProductDetail = () => {
  const { id } = useParams();
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const { product, loading } = useProduct(id);
  const { products: allProducts } = useProducts();
  
  // Helper to get localized text
  const getText = (field) => {
    if (typeof field === 'object' && field !== null && (field.en || field.tr)) {
      return field[lang] || field.en || '';
    }
    return field || '';
  };

  // Helper to get localized specs
  const getSpecs = (specs) => {
    if (typeof specs === 'object' && specs !== null && (specs.en || specs.tr)) {
      return specs[lang] || specs.en || {};
    }
    return specs || {};
  };

  // Dynamic SEO meta tags per product
  useEffect(() => {
    if (!product) return;
    const name = getText(product.displayName) || product.name;
    const model = product.model || '';
    const desc = getText(product.shortDesc) || getText(product.fullDesc) || '';
    const category = product.category || '';
    
    document.title = `${model || name} | VIYERO ${category}`;
    
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', desc.slice(0, 160));
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', `${model || name} | VIYERO`);
    
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', desc.slice(0, 160));
    
    if (product.images?.[0]) {
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) ogImage.setAttribute('content', product.images[0]);
    }

    return () => { document.title = 'VIYERO | Ergonomic Solutions'; };
  }, [product, lang]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-16 text-center">
          <div className="animate-pulse text-gray-400">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h1>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const isGaming = product.type === 'gaming';

  const categoryLabel = lang === 'tr' ? ({
    'Gaming Desks': 'Oyuncu Masaları',
    'Gaming Monitor Arms': 'Monitör Kolları',
    'Gaming Accessories': 'Aksesuarlar',
    'Monitor Arms': 'Monitör Kolları',
    'TV Stands': 'TV Standları',
    'Mobile TV Stands': 'Mobil TV Standları',
    'Dyson Stands': 'Dyson Standları',
    'Accessories': 'Aksesuarlar'
  }[product.category] || product.category) : product.category;
  const backUrl = isGaming
    ? `/gaming?category=${encodeURIComponent(product.category)}`
    : `/office?category=${encodeURIComponent(product.category)}`;

  return (
    <div className={`min-h-screen ${isGaming ? 'bg-gray-950' : 'bg-white'}`}>
      <Header />

      <div className="pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link to={backUrl}>
            <Button 
              variant="ghost" 
              className={`mb-8 ${isGaming ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {lang === 'tr' ? `${categoryLabel} Kategorisine Dön` : `Back to ${categoryLabel}`}
            </Button>
          </Link>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div>
              {(() => {
                // Use language-specific images if available
                const productImages = (lang === 'tr' && product.imagesTr) ? product.imagesTr : product.images;
                return (
                  <>
                    <div className="rounded-2xl overflow-hidden mb-4 aspect-[4/3] cursor-pointer" onClick={() => { setLightboxIdx(selectedImage); setLightboxOpen(true); }}>
                      <img
                        src={productImages[selectedImage]}
                        alt={product.name}
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {productImages.length > 1 && (
                      <div className="grid grid-cols-2 gap-4">
                        {productImages.map((img, idx) => (
                          <button
                            key={idx}
                            onClick={() => { setLightboxIdx(idx); setLightboxOpen(true); }}
                            className={`rounded-lg overflow-hidden aspect-[4/3] border-2 transition-all cursor-pointer ${
                              selectedImage === idx
                                ? isGaming ? 'border-purple-600' : 'border-blue-600'
                                : 'border-transparent hover:border-gray-300'
                            }`}
                          >
                            <img
                              src={img}
                              alt={`${product.name} ${idx + 1}`}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Product Info */}
            <div>
              <div className="mb-4">
                <span className={`text-sm font-medium uppercase tracking-wide ${
                  isGaming ? 'text-purple-400' : 'text-blue-600'
                }`}>
                  {lang === 'tr' ? (
                    {
                      'Gaming Desks': 'Oyuncu Masaları',
                      'Gaming Monitor Arms': 'Monitör Kolları',
                      'Gaming Accessories': 'Aksesuarlar',
                      'Monitor Arms': 'Monitör Kolları',
                      'TV Stands': 'TV Standları',
                      'Mobile TV Stands': 'Mobil TV Standları',
                      'Dyson Stands': 'Dyson Standları',
                      'Accessories': 'Aksesuarlar'
                    }[product.category] || product.category
                  ) : product.category}
                </span>
              </div>
              
              <h1 className={`text-4xl md:text-5xl font-bold mb-4 whitespace-pre-line ${
                isGaming ? 'text-white' : 'text-gray-900'
              }`}>
                {getText(product.displayName) || product.name}
              </h1>
              
              {/* Model Number Subheader */}
              {product.model && (
                <h2 className={`text-xl md:text-2xl font-semibold mb-6 ${
                  isGaming ? 'text-purple-400' : 'text-blue-600'
                }`}>
                  {product.model}
                </h2>
              )}
              
              <p className={`text-lg mb-8 ${
                isGaming ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {getText(product.fullDesc)}
              </p>

              {/* Features - Only show for products with features array */}
              {product.features && product.features.length > 0 && (
                <div className="mb-8">
                  <h3 className={`text-xl font-bold mb-4 ${
                    isGaming ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('product.keyFeatures')}
                  </h3>
                  <ul className="space-y-3">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                          isGaming ? 'text-purple-400' : 'text-blue-600'
                        }`} />
                        <span className={isGaming ? 'text-gray-300' : 'text-gray-700'}>
                          {getText(feature)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Specifications Table */}
              <div className="mb-8">
                <h3 className={`text-xl font-bold mb-4 ${
                  isGaming ? 'text-white' : 'text-gray-900'
                }`}>
                  {t('product.specifications')}
                </h3>
                <div className={`overflow-x-auto rounded-lg border ${
                  isGaming ? 'border-gray-800' : 'border-gray-200'
                }`}>
                  <table className="w-full">
                    <tbody>
                      {Object.entries(getSpecs(product.specs)).map(([key, value], idx) => (
                        <tr key={key} className={`border-b last:border-0 ${
                          isGaming ? 'border-gray-800' : 'border-gray-200'
                        } ${idx % 2 === 0 ? (isGaming ? 'bg-gray-900/50' : 'bg-gray-50') : ''}`}>
                          <td className={`px-4 py-3 font-semibold ${
                            isGaming ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            {key}
                          </td>
                          <td className={`px-4 py-3 ${
                            isGaming ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* CTA */}
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className={`w-full ${
                    isGaming 
                      ? 'bg-purple-600 hover:bg-purple-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white`}
                >
                  {t('product.contactButton')}
                </Button>
              </Link>
            </div>
          </div>

          {/* Feature Images Section */}
          {product.featureImages && product.featureImages.length > 0 && (
            <div className="mt-16">
              <h3 className={`text-2xl md:text-3xl font-bold mb-8 text-center ${
                isGaming ? 'text-white' : 'text-gray-900'
              }`}>
                {t('product.productFeatures') || 'Product Features'}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {product.featureImages.map((featureImg, idx) => (
                  <div 
                    key={idx} 
                    className={`rounded-xl overflow-hidden border transition-all hover:shadow-lg ${
                      isGaming ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={featureImg.url}
                        alt={getText(featureImg.title)}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4 text-center">
                      <h4 className={`font-bold text-lg mb-1 ${
                        isGaming ? 'text-white' : 'text-gray-900'
                      }`}>
                        {getText(featureImg.title)}
                      </h4>
                      <p className={`text-sm ${
                        isGaming ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {getText(featureImg.description)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Related Products Section */}
          {product.relatedProducts && product.relatedProducts.length > 0 && (
            <div className="mt-16">
              <h3 className={`text-2xl font-bold mb-6 ${
                isGaming ? 'text-white' : 'text-gray-900'
              }`}>
                Other Models in {product.category}
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {product.relatedProducts.map((relatedId) => {
                  const relatedProduct = allProducts.find(p => p.id === relatedId);
                  if (!relatedProduct) return null;
                  
                  return (
                    <Link key={relatedId} to={`/product/${relatedId}`} className="group">
                      <div className={`rounded-lg overflow-hidden border transition-all hover:shadow-lg ${
                        isGaming ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
                      }`}>
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={relatedProduct.images[0]}
                            alt={getText(relatedProduct.displayName) || relatedProduct.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4">
                          <p className={`text-sm font-medium mb-1 ${
                            isGaming ? 'text-purple-400' : 'text-blue-600'
                          }`}>
                            {relatedProduct.model}
                          </p>
                          <h4 className={`font-bold mb-2 ${
                            isGaming ? 'text-white' : 'text-gray-900'
                          }`}>
                            {getText(relatedProduct.displayName) || relatedProduct.name}
                          </h4>
                          <p className={`text-sm mb-3 line-clamp-2 ${
                            isGaming ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {getText(relatedProduct.shortDesc)}
                          </p>
                          <div className={`flex items-center text-sm font-medium ${
                            isGaming ? 'text-purple-400' : 'text-blue-600'
                          }`}>
                            View Details
                            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* Lightbox */}
      {lightboxOpen && (() => {
        const productImages = (lang === 'tr' && product.imagesTr) ? product.imagesTr : product.images;
        const total = productImages.length;
        return (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
            <button className="absolute top-4 right-4 text-white/70 hover:text-white z-50 p-2" onClick={() => setLightboxOpen(false)} data-testid="lightbox-close">
              <X className="h-8 w-8" />
            </button>
            {total > 1 && (
              <button className="absolute left-4 text-white/70 hover:text-white z-50 p-2" onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + total) % total); }} data-testid="lightbox-prev">
                <ChevronLeft className="h-10 w-10" />
              </button>
            )}
            {total > 1 && (
              <button className="absolute right-4 text-white/70 hover:text-white z-50 p-2" onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % total); }} data-testid="lightbox-next">
                <ChevronRight className="h-10 w-10" />
              </button>
            )}
            <img
              src={productImages[lightboxIdx]}
              alt={`${product.name} ${lightboxIdx + 1}`}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            {total > 1 && (
              <div className="absolute bottom-6 flex gap-2">
                {productImages.map((_, idx) => (
                  <button key={idx} onClick={(e) => { e.stopPropagation(); setLightboxIdx(idx); }} className={`w-2.5 h-2.5 rounded-full transition-all ${idx === lightboxIdx ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/70'}`} />
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default ProductDetail;
